import React from 'react';
import { TableColumn } from 'react-data-table-component';
import CustomMaterialMenu from '../../../Component/DataGrid/Plugins/CustomMaterialMenu/CustomMaterialMenu';
import DataGrid from '../../../Component/DataGrid/DataGrid';
import { SyncContext, SyncContextType } from '../../../Contexts/BatchSyncContext';
import { ShowAlert } from '../../../Component/ShowAlert';
import UserAndPermissionService from '../../../Services/UserAndPermissionService';
import { CorridorListProps, CorridorListState, ICorridorList } from './Interface';
import CorridorService from '../../../Services/CorridorService';
import { Status } from '../../../Component/Reusables/Utility';
import { loadFromLocalStorage } from '../../../Common/Validation/LocalStorage';
import { hasFlag } from 'country-flag-icons';
import * as Flags from 'country-flag-icons/react/3x2';
import { Switch } from '@mui/material';
import { ShowAlertConfirm } from '../../../Component/ShowAlertConfirm';

class CorridorList extends React.Component<CorridorListProps, CorridorListState> {
    static contextType = SyncContext;
    context!: SyncContextType;

    private tableColumns: TableColumn<ICorridorList>[] = [
        {
            name: 'Country ID',
            selector: row => row.countryId,
            sortable: true,
            wrap: true,
            minWidth: "250px",
            omit: true
        },
        {
            name: 'Country Name',
            cell: (row) => this.renderCountryWithFlag(row),
            sortable: true,
            id: "countryName",
            wrap: true,
            minWidth: "200px"
        },
        {
            name: 'Currency Code',
            selector: (row) => `${row.currencyCode} - (${row.currencySymbol})`,
            sortable: true,
            id: "currencyCode",
            wrap: true,
            minWidth: "200px"
        },
        {
            name: 'Currency Name',
            selector: row => row.currencyName,
            sortable: true,
            id: "currencyName",
            wrap: true,
            minWidth: "200px"
        },
        {
            name: 'Status',
            selector: row => row.isActive ? Status.ACTIVE : Status.INACTIVE,
            sortable: true,
            id: "status",
            cell: (row) => (
                <div >
                    <Switch
                        checked={row.isActive}
                        onChange={(e) => this.handleStatusToggle(row, e.target.checked)}
                        color="secondary"
                    />
                    <span className={`status-label ${row.isActive ? 'active' : 'inactive'}`}>
                        {row.isActive ? Status.ACTIVE : Status.INACTIVE}
                    </span>
                </div>
            )
        }
    ];

    constructor(props_: CorridorListProps) {
        super(props_);

        this.state = {
            accessToken: "",
            corridorList: [],
            currentRowsPerPage: 10,
            currentPage: 1,
            totalRows: 0,
            searchText: "",
            selectedRow: null,
            corridor: {
                isVisible: true,
                isActiveCorridorAllowed: true,
                isInactiveCorridorAllowed: true,
            },
        }
        this.getCorridorList = this.getCorridorList.bind(this);
    }

    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            const profile_ = await loadFromLocalStorage('profile');

            if (!accessToken_ || !profile_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            const accessToken = accessToken_.toString();
            const userProfile = typeof profile_ === 'string' ? JSON.parse(profile_) : profile_;

            this.setState({ accessToken }, async () => {
                try {
                    if (userProfile?.roleId) {
                        const permissionResponseRaw = await UserAndPermissionService.getPermissions(accessToken, userProfile?.roleId);
                        const permissionResponse = await permissionResponseRaw.json();

                        if (!permissionResponse?.permissions?.corridor?.isVisible) {
                            console.warn("Permission denied, logging out...");
                            localStorage.clear();
                            window.location.href = '/';
                            return;
                        }

                        const permissions = permissionResponse.permissions

                        this.context.dispatch({
                            type: "SET_PERMISSION", payload: {
                                permissions
                            },
                        });

                        const corridor = permissions.corridor

                        this.setState({ corridor }, () => {
                            this.getCorridorList();
                        });
                    } else {
                        this.getCorridorList();
                    }
                } catch (error) {
                    console.error("Error fetching permissions:", error);
                }
            });
        } catch (error) {
            console.error("Error in componentDidMount:", error);
            localStorage.clear();
            window.location.href = '/';
        }
    }


    //  function to render flag and country name together
    public renderCountryWithFlag = (row: ICorridorList) => {
        const countryId = row.countryId;

        if (countryId && hasFlag(countryId)) {
            // Get the flag component for this country
            const FlagComponent = Flags[countryId as keyof typeof Flags];

            if (FlagComponent) {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FlagComponent
                            style={{
                                width: '24px',
                                height: '18px',
                                borderRadius: '2px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                        />
                        <span>{row.countryName}</span>
                    </div>
                );
            }
        }

        // return country name if flog not available
        return <span>{row.countryName}</span>;
    };

    private handleStatusToggle(row_: ICorridorList, isActive: boolean) {
        // If activate - save directly without confirmation
        if (isActive) {
            this.handleStatusUpdate(row_, isActive);
        } else {
            // If inactive - show confirmation popup
            ShowAlertConfirm(
                "Deactivate Corridor",
                `Are you sure you want to deactivate the ${row_.countryName} corridor? `,
                "warning",
                false,  // allowOutsideClick
                false,  // allowEscapeKey
                true,   // showConfirmButton
                true,   // showCancelButton
                "Yes",
                "Cancel"
            ).then((confirmed: boolean) => {
                if (confirmed) {
                    this.handleStatusUpdate(row_, false);
                }
            });
        }
    }

    private getCorridorList() {
        let pageIndex: number = this.state.currentPage;
        let pagination: any = {
            pageNumber: pageIndex,
            recordPerPage: this.state.currentRowsPerPage
        };

        if (this.state.searchText) {
            pagination["searchText"] = this.state.searchText
        }

        CorridorService.getCorridorList(this.state.accessToken, pagination).then(tr => {
            if (tr.ok) {
                tr.json().then(resp => {
                    console.log(resp)
                    this.setState({
                        corridorList: resp.data,
                        totalRows: resp.totalRecords
                    })
                });
            }
        });
    }

    // Update the status of a corridor: Active or Inactive
    private async handleStatusUpdate(row_: ICorridorList, isActive: boolean) {
        try {
            const postData = {
                countryId: row_.countryId,
                corridorStatus: isActive ? 1 : 0
            };

            const resp = await CorridorService.updateCorridorStatus(this.state.accessToken, postData);

            if (resp?.ok) {
                ShowAlert("PassportPay", `Corridor status has been successfully updated.`, "success")
                    .then(() => { this.getCorridorList(); });
            }
        } catch (error) {
            console.error("Error updating Corridor status:", error);
            ShowAlert("Error", "An error occurred while updating the Corridor status. Please try again.", "error");
        }
    }

    public render() {
        const {
        } = this.state.corridor;
        return (
            <>
                <div className="body-sub-container table-container">
                    <DataGrid
                        title='Corridors'
                        search='Currency'
                        defaultSearch={this.state.searchText}
                        onSearch={(searchText_: string) => {
                            this.setState({
                                searchText: searchText_,
                                currentPage: 1
                            }, () => {
                                this.getCorridorList();
                            });
                        }}
                        columns={this.tableColumns}
                        data={this.state.corridorList}
                        onChangePage={(page: number, totalRows: number) => {
                            this.setState({
                                currentPage: page
                            }, () => {
                                this.getCorridorList();
                            })
                        }}
                        onChangeRowsPerPage={(currentRowsPerPage_: number, currentPage_: number) => {
                            this.setState({
                                currentRowsPerPage: currentRowsPerPage_,
                                currentPage: currentPage_
                            }, () => {
                                this.getCorridorList();
                            })
                        }}
                        pagination
                        paginationTotalRows={this.state.totalRows}
                        paginationDefaultPage={1}
                    />
                </div>
            </>
        )
    }
}

export default CorridorList;